import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Row,
  Text,
  TitledText,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { FundAdditionalIncomeAndExpenditureModel } from '@/features/project/projects/_models/fund-additiona-income-and-expenditure';
import { ProjectModel } from '@/features/project/projects/_models/project';
import { useLocaledAmount } from '@/providers';
import { formatValueByUnit } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { convertPriorToList } from '../../_utils/fund-income-and-expenditure-table';
import {
  formatDivergenceAmount,
  formatDivergenceRate,
} from '../../_utils/value-converter';
import { UpdateDivergenceFormDrawer } from '../divergence/UpdateDivergenceFormDrawer';
import { FundAdditionalIncomeAndExpenditureTable } from './table/FundAdditionalIncomeAndExpenditureTable';

type Props = z.infer<typeof ProjectModel>;

const PINPOINT_SHEET_TYPE = 'KS_STANDARD';

export const FundAdditionalIncomeAndExpenditureInformation: FC<Props> = (
  project,
) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { setDrawerState } = useProjectDetailDrawerState();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  const editProjectPermission = useProjectPermission(true);

  const parsedProject = ProjectModel.safeParse(project);
  if (!parsedProject.success) {
    return null;
  }

  const parsed = FundAdditionalIncomeAndExpenditureModel.safeParse(
    parsedProject.data.fundAdditionalIncomeAndExpenditure,
  );
  if (!parsed.success) {
    return null;
  }

  const pastFundAdditionalIncomeAndExpenditureList = convertPriorToList({
    value: parsed.data,
  }).map((v, i) => ({
    ...v,
    fiscalYear: parsedProject.data.fiscalYear - i - 1,
  }));

  const oneYearsAgo = pastFundAdditionalIncomeAndExpenditureList.find(
    (v) => v.fiscalYear === parsedProject.data.fiscalYear - 1,
  );
  const twoYearsAgo = pastFundAdditionalIncomeAndExpenditureList.find(
    (v) => v.fiscalYear === parsedProject.data.fiscalYear - 2,
  );

  const executionDivergences =
    parsedProject.data.fundAdditionalAttributes?.executionDivergences;

  const { oneYearAgoDivergence, twoYearsAgoDivergence } = parsed.data;

  return (
    <Flex vertical gap={24}>
      <Flex vertical gap={8}>
        <FundAdditionalIncomeAndExpenditureTable
          fiscalYear={parsedProject.data.fiscalYear}
          data={parsed.data}
        />
      </Flex>

      <Divider />

      <Flex justify="space-between">
        <Text variant="h3" text={t('situation-of-divergence-in-execution')} />
        {editProjectPermission.data && !project.isLocked && (
          <PublishedProjectMutableGuard>
            <Button
              type="primary"
              onClick={() => {
                setDrawerState({
                  open: true,
                  type: 'divergence',
                });
              }}
            >
              {t('edit')}
            </Button>
          </PublishedProjectMutableGuard>
        )}
      </Flex>
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType: PINPOINT_SHEET_TYPE,
          ksStandard: {
            path: {
              project: parsedProject.data.id,
            },
            field:
              'ks.standard.situation-of-divergence-in-execution-two-years-ago',
          },
        })}
      >
        <Card>
          <Row gutter={[0, 24]}>
            <Col span={12}>
              <TitledText
                title={t('estimated-business-expenses-with-year', {
                  year: parsedProject.data.fiscalYear - 2,
                })}
                text={formatValueByUnit(
                  twoYearsAgoDivergence?.estimatedBusinessExpense,
                  {
                    operation: 'round',
                    unit,
                    showUnitLabel: false,
                  },
                )}
              />
            </Col>
            <Col span={12}>
              <TitledText
                title={t('business-expenses-with-year', {
                  year: parsedProject.data.fiscalYear - 2,
                })}
                text={formatValueByUnit(
                  twoYearsAgoDivergence?.businessExpense,
                  {
                    operation: 'round',
                    unit,
                    showUnitLabel: false,
                  },
                )}
              />
            </Col>
            <Col span={12}>
              <TitledText
                title={t('divergence-amount')}
                text={formatDivergenceAmount({
                  divergenceAmount: twoYearsAgoDivergence?.divergenceAmount,
                  estimatedBusinessExpense:
                    twoYearsAgoDivergence?.estimatedBusinessExpense,
                  businessExpense: twoYearsAgoDivergence?.businessExpense,
                  isDebugMode,
                })}
              />
            </Col>
            <Col span={12}>
              <TitledText
                title={t('divergence-rate')}
                text={formatDivergenceRate(
                  twoYearsAgoDivergence?.divergenceRate,
                )}
              />
            </Col>
            <Col span={24}>
              <TitledText
                title={t('reason-for-divergence')}
                text={executionDivergences?.[1]?.reason}
              />
            </Col>
          </Row>
        </Card>
      </PinpointMarker>
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType: PINPOINT_SHEET_TYPE,
          ksStandard: {
            path: {
              project: parsedProject.data.id,
            },
            field:
              'ks.standard.situation-of-divergence-in-execution-one-years-ago',
          },
        })}
      >
        <Card>
          <Row gutter={[0, 24]}>
            <Col span={12}>
              <TitledText
                title={t('estimated-business-expenses-with-year', {
                  year: parsedProject.data.fiscalYear - 1,
                })}
                text={formatValueByUnit(
                  oneYearAgoDivergence?.estimatedBusinessExpense,
                  {
                    operation: 'round',
                    unit,
                    showUnitLabel: false,
                  },
                )}
              />
            </Col>
            <Col span={12}>
              <TitledText
                title={t('business-expenses-with-year', {
                  year: parsedProject.data.fiscalYear - 1,
                })}
                text={formatValueByUnit(oneYearAgoDivergence?.businessExpense, {
                  operation: 'round',
                  unit,
                  showUnitLabel: false,
                })}
              />
            </Col>
            <Col span={12}>
              <TitledText
                title={t('divergence-amount')}
                text={formatDivergenceAmount({
                  divergenceAmount: oneYearAgoDivergence?.divergenceAmount,
                  estimatedBusinessExpense:
                    oneYearAgoDivergence?.estimatedBusinessExpense,
                  businessExpense: oneYearAgoDivergence?.businessExpense,
                  isDebugMode,
                })}
              />
            </Col>
            <Col span={12}>
              <TitledText
                title={t('divergence-rate')}
                text={formatDivergenceRate(
                  oneYearAgoDivergence?.divergenceRate,
                )}
              />
            </Col>
            <Col span={24}>
              <TitledText
                title={t('reason-for-divergence')}
                text={executionDivergences?.[0]?.reason}
              />
            </Col>
          </Row>
        </Card>
      </PinpointMarker>
      {editProjectPermission.data && (
        <UpdateDivergenceFormDrawer
          hasOneYearsAgo={!!oneYearsAgo}
          hasOneYearsAgoProject={!!oneYearsAgo?.projectId}
          hasTwoYearsAgo={!!twoYearsAgo}
          hasTwoYearsAgoProject={!!twoYearsAgo?.projectId}
          fiscalYear={parsedProject.data.fiscalYear}
          executionDivergences={executionDivergences}
        />
      )}
    </Flex>
  );
};
